import { HttpEvent, HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { AuthStateService } from '../auth/auth-state.service';
import { currentBrowserUrl, rememberSignInReturn, signInQuery } from '../auth/sign-in-return';
import { SESSION_ENDED, TOKEN_ACQUISITION_FAILED } from './sign-in-failure';
import { TenantContextService } from '../tenant/tenant-context.service';
import { environment } from '../../../environments/environment';

const apiBase = environment.api.replace(/\/+$/, '');

function isApiRequest(url: string): boolean {
  return url === apiBase || url.startsWith(apiBase + '/');
}

/**
 * HTTP interceptor that attaches Bearer tokens to API requests
 * and handles authentication errors gracefully.
 *
 * Key behaviors:
 * - Only intercepts requests to the configured API endpoint
 * - Acquires tokens silently from Auth0
 * - Handles 401 and Auth0 auth-sdk errors by redirecting to login
 * - Treats 403 as authorization failure (no forced logout)
 * - Does NOT treat 500/other server errors as auth failures
 * - Prevents duplicate redirects via AuthStateService
 */
export const authTokenInterceptor: HttpInterceptorFn = (
  req,
  next
): Observable<HttpEvent<unknown>> => {
  if (!isApiRequest(req.url)) {
    return next(req);
  }

  const auth = inject(AuthService);
  const authState = inject(AuthStateService);
  const router = inject(Router);
  const tenantContext = inject(TenantContextService);

  // If already redirecting to login, reject immediately to prevent request spam
  if (authState.isRedirecting()) {
    console.warn('[AuthInterceptor] BLOCKED - Redirect in progress');
    return throwError(() => createAuthError(req.url, 'Redirect in progress'));
  }

  // Check if user is logged in before attempting token acquisition
  if (!auth.isLoggedInSync()) {
    return next(req); // Continue without token, let backend handle auth
  }

  return from(auth.getAccessTokenSilently()).pipe(
    switchMap((token) => {
      const tenantKey = tenantContext.currentTenantKey().trim();
      const authorizedReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          ...(tenantKey && !req.headers.has('X-Tenant-Key') ? { 'X-Tenant-Key': tenantKey } : {}),
        },
      });
      return next(authorizedReq);
    }),
    catchError((err) => {
      // HTTP auth failures: only 401 should force logout.
      if (err instanceof HttpErrorResponse) {
        if (err.status === 401) {
          return handleError(err, req.url, auth, authState, router);
        }
        return throwError(() => err);
      }
      // Non-HTTP failures may come from Auth0 SDK (e.g. login_required).
      return handleError(err, req.url, auth, authState, router);
    })
  );
};

/**
 * Handle errors from token acquisition or HTTP responses.
 */
function handleError(
  err: unknown,
  url: string,
  auth: AuthService,
  authState: AuthStateService,
  router: Router
): Observable<never> {
  const httpError = err instanceof HttpErrorResponse ? err : null;

  // Determine if this is an authentication failure
  const isAuthError = isAuthenticationError(err);

  // Only redirect for actual auth errors, NOT for server errors (500, etc.)
  if (isAuthError && !authState.isRedirecting()) {
    initiateLogout(auth, authState, router);
  }

  // Return the original error, or a status-0 one for a token failure: SESSION_ENDED when the
  // session needs a sign-in, so callers can tell it from a network or other token failure.
  return throwError(
    () =>
      httpError ?? createAuthError(url, isAuthError ? SESSION_ENDED : TOKEN_ACQUISITION_FAILED, 0)
  );
}

/**
 * Check if an error represents an authentication failure.
 *
 * Returns true for:
 * - HTTP 401 (Unauthorized)
 * - Auth0 SDK errors indicating login is required
 *
 * Returns false for:
 * - HTTP 500 and other server errors
 * - Network errors
 * - Any other non-auth errors
 */
function isAuthenticationError(err: unknown): boolean {
  // HTTP 401 is an auth error. 403 is an authorization error.
  if (err instanceof HttpErrorResponse) {
    return err.status === 401;
  }

  // Check for Auth0 SDK errors that indicate login is required
  const error = err as { error?: string; message?: string } | null;
  if (!error) return false;

  const code = (error.error || '').toLowerCase();
  const msg = (error.message || '').toLowerCase();

  return (
    code === 'login_required' ||
    code === 'consent_required' ||
    code === 'invalid_grant' ||
    code === 'missing_refresh_token' ||
    /login required|consent required|invalid grant|session expired/.test(msg)
  );
}

function initiateLogout(auth: AuthService, authState: AuthStateService, router: Router): void {
  if (!authState.acquireRedirectLock()) {
    return; // Lock already held
  }

  // Remember the page before logout to restore user intent after authentication
  const returnUrl = rememberSignInReturn(currentBrowserUrl());

  auth
    .logout(window.location.origin)
    .catch(() => {
      // If logout fails, navigate to login directly
      router.navigate(['/login'], { queryParams: signInQuery(returnUrl, 'session_expired') });
    })
    .finally(() => {
      // Always release the lock after logout attempt
      authState.clearRedirectLockAfterDelay(1000);
    });
}

/**
 * Create a standardized auth error response.
 * Use status 0 for non-HTTP failures (e.g. token acquisition) so callers can
 * distinguish from true 401 Unauthorized.
 */
function createAuthError(url: string, message: string, status = 401): HttpErrorResponse {
  return new HttpErrorResponse({
    error: message,
    status,
    statusText: status === 401 ? 'Unauthorized' : 'Unknown',
    url,
  });
}
